import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, firstValueFrom, merge, Observable, of, Subscription, throwError } from 'rxjs';
import {
    catchError,
    distinctUntilChanged,
    filter,
    map,
    shareReplay,
    switchMap,
    take,
    takeUntil,
    tap,
} from 'rxjs/operators';
import { ErrorPopupService } from '../../error-popup/error-popup.service';
import { isNotNullOrUndefined } from '../../helper/helper';
import { DocumentUserEntity, DocumentUserEntityFromBackend } from "../entities/document-user.entity";
import { FileEntity, FileEntityFromFileBackend, FileTypeFromFileBackend } from '../entities/file.entity';
import { DocumentUserType } from '../graphql-types';
import { retryWithBackoff } from '../helper/http-service.helper';
import { State } from '../State';
import { BaseActionTypes } from '../State/actions/base.actions';
import { FileUserActionTypes } from '../State/actions/file-user';
import { FilesActionTypes } from '../State/actions/files.actions';
import { getToken } from '../State/selectors/base.selectors';
import { getFileById, getFiles, getFilesByFolderId } from '../State/selectors/files.selectors';
import { HttpService } from './http.service';
import { DocumentType as DocumentDetectionDocumentType } from '@dave/types/dist/proto/documentDetection/documentDetection';
import { AppGatewayService } from './app-gateway.service';
import { ToastrService } from 'ngx-toastr';

@Injectable({
    providedIn: 'root',
})
export class FileDataService implements OnDestroy {
    /**
     *  key is folderId
     *  value is Date of latest updatedAt
     * @private
     */
    private folderCacheMap: Map<number, Date>;
    private pendingRequestsBuffer: Map<string, Observable<FileTypeFromFileBackend[]>>;
    private baseUrl: string;
    private clearSub: Subscription;
    constructor(
        private http: HttpClient, private store: Store<State>,
        private gatewayHttpService: HttpService,
        private actions$: Actions,
        private errorPopup: ErrorPopupService,
        private gatewayService: AppGatewayService,
        private toastrService: ToastrService,
    ) {
        this.reset();
        this.clearSub = this.actions$.pipe(ofType(BaseActionTypes.InitStore)).subscribe(() => this.reset());
    }
    ngOnDestroy(): void {
        this.clearSub.unsubscribe();
    }
    private reset() {
        this.pendingRequestsBuffer = new Map<string, Observable<FileTypeFromFileBackend[]>>();
        this.folderCacheMap = new Map<number, Date>();
        this.baseUrl = this.gatewayHttpService.GetUrl('files', 'file');
    }
    private getFilesFromBackend(options: {
        folderId?: number;
        updatedAtSince?: Date;
        documentIds?: number[];
        withEmailInline?: boolean;
        withSubFolder?: boolean;
        withDocumentDetectionResult?: boolean;
    }): Observable<FileTypeFromFileBackend[]> {
        let headers = new HttpHeaders();
        headers = headers.set('Content-Type', 'application/json');
        headers = headers.set('Authorization', 'Bearer ' + localStorage.getItem('token'));
        const params: { [key: string]: string } = Object.create(null);

        if (options.folderId != null) {
            // null für die Files die im imaginären Hauptordner liegen, also keine FolderId haben
            params['parent_folder'] = options.folderId ? options.folderId + '' : 'null';
        }
        if (options.updatedAtSince) {
            params['since'] = options.updatedAtSince.toISOString();
        }
        if (options.withDocumentDetectionResult) {
            params['with_document_detection_data'] = 'true';
        }
        if (options.documentIds) {
            params['document_ids'] = options.documentIds.join(',');
        }
        if (options.withEmailInline) {
            params['with_email_inline'] = 'true';
        }
        if (options.withSubFolder) {
            params['with_sub_folder'] = 'true';
        }
        const paramString = `${
            Object.keys(params).length
                ? '?' +
                  Object.keys(params)
                      .map((k) => k + '=' + params[k])
                      .join('&')
                : ''
        }`;
        if (!this.pendingRequestsBuffer.has(paramString)) {
            this.pendingRequestsBuffer.set(
                paramString,
                this.http
                    .get<FileTypeFromFileBackend[]>(this.baseUrl + paramString, {
                        headers,
                    })
                    .pipe(
                        retryWithBackoff(),
                        catchError((err, caught) => {
                            this.errorPopup.OpenErrorPopup();
                            this.store.dispatch(
                                BaseActionTypes.ErrorAction({
                                    Payload: {
                                        ToasterMessage: 'Dateien Abrufen fehlgeschlagen',
                                        Err: err,
                                        Caught: caught,
                                    },
                                }),
                            );
                            return of([]);
                        }),
                        take(1),
                        tap(() => this.pendingRequestsBuffer.delete(paramString)),
                        shareReplay({ refCount: true, bufferSize: 1 }),
                    ),
            );
        }
        return this.pendingRequestsBuffer.get(paramString);
    }
    public async pollFilesAndLinksByVersionId(versionId: number) {
        const files = await firstValueFrom(this.store.select(getFiles).pipe(map(files => files.filter(f => f.Versions.some(v => v.Id === versionId)))));
        files.forEach(f => firstValueFrom(this.GetFileById$(f.Id, true)))
    }
    public GetFileById$(id: number, reload = false, config?: { withEmailInline?: boolean; withDocumentDetectionResult?: boolean }): Observable<FileEntity> {
        if (!id) {
            console.error('Id is required');
        }
        return this.store.select(getFileById({ id })).pipe(
            take(1),
            switchMap((file) => {
                if (file && !reload && !config?.withDocumentDetectionResult) {
                    return this.store.select(getFileById({ id }));
                } else {
                    const req = this.getFilesFromBackend({ documentIds: [id], ...config }).pipe(shareReplay({ refCount: true, bufferSize: 1 }));
                    return req.pipe(
                        switchMap((files) => {
                            if (files.length !== 1) {
                                console.error('File mit Id ' + id + ' konnte nicht abgerufen werden.');
                                return throwError(() => new Error('File mit Id ' + id + ' konnte nicht abgerufen werden.'));
                            }
                            this.handleFileReturn(files);
                            return this.store.select(getFileById({ id }));
                        })
                    );
                }
            }),
        );
    }
    public GetFilesByFolderIdNoCache$(folderId: number, withSubFolder = false): Promise<FileEntity[]> {
        return firstValueFrom(this.getFilesFromBackend({ folderId, withSubFolder })).then((files) => this.handleFileReturn(files));
    }

    /**
     * Retrieves files by their ids. only missing ones are polled from server
     *
     * @param {number[]} ids - The array of file ids to retrieve.
     * @param {Object} [config] - Additional configuration options.
     * @param {boolean} [config.withEmailInline] - Specifies whether to include email inline.
     * @returns {Observable<FileEntity[]>} - An observable that emits an array of file entities that match the provided ids.
     */
    public GetFilesById(ids: number[], config?: { withEmailInline: boolean }): Observable<FileEntity[]> {
        return ids?.length
            ? combineLatest(ids.map((id) => this.store.select(getFileById({ id })))).pipe(
                  take(1),
                  switchMap((documents) => {
                      const availableIds = documents.filter(isNotNullOrUndefined).map((d) => d.Id);
                      const missingIds = ids.filter((id) => !availableIds.includes(id));
                      if (!missingIds.length) {
                          return this.store.select(getFiles).pipe(map((files) => files.filter((f) => ids.includes(f.Id))));
                      }
                      const req = this.getFilesFromBackend({ documentIds: missingIds, ...config }).pipe(shareReplay({ refCount: true, bufferSize: 1 }));
                      req.subscribe((files) => this.handleFileReturn(files));
                      return req.pipe(
                          switchMap(() => this.store.select(getFiles)),
                          map((files) => files.filter((f) => ids.includes(f.Id))),
                      );
                  }),
              )
            : of([]);
    }
    public GetFilesFromFolder$(folderId: number, reload = false): Observable<FileEntity[]> {
        if (folderId == null) {
            throw new Error('FolderId is required (can be 0 for root folder)');
        }
        if (this.folderCacheMap.has(folderId) && !reload) {
            return this.store.select(getFilesByFolderId({ folderId: folderId || null }));
        } else {
            const now = new Date();
            const req = this.getFilesFromBackend({ folderId }).pipe(shareReplay({ refCount: true, bufferSize: 1 }));
            req.subscribe((files) => this.handleFileReturn(files, folderId, now));
            return req.pipe(switchMap(() => this.store.select(getFilesByFolderId({ folderId: folderId || null }))));
        }
    }
    public GetFilesStateFromFolder$(folderId: number, reload = true): Observable<FileEntity[]> {
        if (folderId == null) {
            throw new Error('FolderId is required (can be 0 for root folder)');
        }
        if (this.folderCacheMap.has(folderId)) {
            if (reload) {
                const now = new Date();
                this.getFilesFromBackend({ folderId, updatedAtSince: this.folderCacheMap.get(folderId) }).subscribe((files) => this.handleFileReturn(files, folderId, now));
            }
            return this.store.select(getFilesByFolderId({ folderId: folderId || null }));
        } else {
            const now = new Date();
            const req = this.getFilesFromBackend({ folderId }).pipe(shareReplay({ refCount: true, bufferSize: 1 }));
            req.subscribe((files) => this.handleFileReturn(files, folderId, now));
            return req.pipe(switchMap(() => this.store.select(getFilesByFolderId({ folderId: folderId || null }))),
                distinctUntilChanged((a, b) => a.length === b.length && a.every((e, index) => e === b[index])));
        }
    }
    private handleFileReturn(files: FileTypeFromFileBackend[], folderId?: number, timeStamp?: Date) {
        const fileEntities = files.map(FileEntityFromFileBackend);
        if (files) {
            if (folderId) {
                this.folderCacheMap.set(folderId, timeStamp);
            }
            if (files.length) {
                this.store.dispatch(FilesActionTypes.UpdateMany({ Payload: fileEntities }));
            }
        }
        return fileEntities;
    }
    public getUserForDocument(fileId: number): Observable<DocumentUserEntity[]> {
        return merge(
            this.store.select(getToken).pipe(
                filter(token => !!token),  //logout fix
                switchMap((token) => {
                    let headers = new HttpHeaders();
                    headers = headers.set('Content-Type', 'application/json');
                    headers = headers.set('Authorization', 'Bearer ' + token);

                    return this.http.get<DocumentUserType[]>(this.gatewayHttpService.GetUrl('document-user', 'file') + '?document_id=' + fileId, {
                        headers,
                    });
                }),
                map((res) => res?.map((u) => DocumentUserEntityFromBackend(u))),
            ),
            this.actions$.pipe(
                ofType(FileUserActionTypes.Update),
                filter((p) => p.Payload.documentId === fileId),
                map((p) => p.Payload.users),
            ),
        );
    }

    public async forceDetectDocumentDataAs(type: DocumentDetectionDocumentType, fileVersionId: number) {
        return this.gatewayService
            .Request({
                FileFileForceDetectDocumentDataAs: {
                    VersionId: fileVersionId.toString(),
                    Type: type,
                },
            })
            .then((res) => {
                if (Object.keys(res?.Errors || {}).length === 0) {
                    this.toastrService.info('Die Dauer beträgt bis zu 5 Minuten.', 'Das Dokument wird ausgelesen.');
                } else {
                    throw res?.Errors;
                }
            })
            .catch((err) => this.store.dispatch(BaseActionTypes.ErrorAction({ Payload: { Err: err, ToasterMessage: 'Dokument auslesen fehlgeschlagen' } })));
    }

    public async setDetectDocumentPrio(versionIds: number[], fileIds: number[], prio?: string){
        return this.gatewayService
            .Request({
                FileFileSetDetectDocumentPrio: {
                    VersionId: versionIds.map((vId) => vId.toString()),
                    DocumentId: fileIds.map((fId) => fId.toString()),
                    Prio: prio ? prio : undefined,
                },
        }).then((res) => {
            if (Object.keys(res?.Errors || {}).length === 0) {
                this.toastrService.info('Die Dauer kann mehrere Minuten betragen.', 'Die Dokumente werden ausgelesen');
            } else {
                throw res?.Errors;
            }
        }).catch((err) => {
            this.store.dispatch(BaseActionTypes.ErrorAction({ Payload: { Err: err, ToasterMessage: 'Auslesen der Dokumente fehlgeschlagen' } }));
        })
    }
    async deleteFile(id: number) {
        this.store.dispatch(FilesActionTypes.DeleteFileRequest({ Payload: { DocumentId: id.toString() } }));
        const res = await firstValueFrom(this.actions$.pipe(ofType(FilesActionTypes.DeleteFileSuccess, FilesActionTypes.DeleteFileFailure)))
        return res.type === FilesActionTypes.DeleteFileSuccess.type;
    }
}
